import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TeamProvider } from "./contexts/TeamContext";
import useChalkEraseReveal from "./lib/useChalkEraseReveal";

import Login from "./pages/Login";
import Forgot from "./pages/Forgot";
import ResetPassword from "./pages/ResetPassword";
import Join from "./pages/Join";
import Onboarding from "./pages/Onboarding";
import AppShell from "./pages/AppShell";
import Chalkboard from "./pages/Chalkboard";
import Clipboard from "./pages/Clipboard";
import Playbook from "./pages/Playbook";
import Roster from "./pages/Roster";
import FamilyDashboard from "./pages/FamilyDashboard";
import ChalkTalk from "./pages/ChalkTalkPage";
import ChalkLines from "./pages/ChalkLines";
import Volunteers from "./pages/Volunteers";
import League from "./pages/League";
import More from "./pages/More";
import Tournaments from "./pages/Tournaments";
import TournamentWorkspace from "./pages/TournamentWorkspace";
import PublicTournament from "./pages/PublicTournament";
import PublicCheckin from "./pages/PublicCheckin";
import PublicResults from "./pages/PublicResults";
import BigScreen from "./pages/BigScreen";
import TournamentPrint from "./pages/TournamentPrint";
import ClaimTeam from "./pages/ClaimTeam";
import ScreenPair from "./pages/ScreenPair";
import TournamentUpgrade from "./pages/TournamentUpgrade";
import TournamentCheckoutMock from "./pages/TournamentCheckoutMock";
import DirectorPlan from "./pages/DirectorPlan";
import DirectorArchive from "./pages/DirectorArchive";

import AdminBilling from "./pages/admin/AdminBilling";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AdminEmails from "./pages/admin/AdminEmails";
import AdminJobs from "./pages/admin/AdminJobs";
import AdminAudit from "./pages/admin/AdminAudit";
import AdminPermissionTests from "./pages/admin/AdminPermissionTests";
import BillingSuccess from "./pages/BillingSuccess";
import PreviewCheckout from "./pages/PreviewCheckout";
import BillingCancelled from "./pages/BillingCancelled";
import Landing from "./pages/Landing";
import TournamentsLanding from "./pages/TournamentsLanding";
import Start from "./pages/Start";
import JoinTeam from "./pages/JoinTeam";
import StaffAccept from "./pages/StaffAccept";
import Upgrade from "./pages/Upgrade";
import AdminVoluntold from "./pages/admin/AdminVoluntold";
import { SHOW_TOURNAMENT_MARKETING } from "./lib/featureFlags";
import Pricing from "./pages/Pricing";
import Coaches from "./pages/Coaches";
import Parents from "./pages/Parents";
import TeamStaff from "./pages/TeamStaff";
import Leagues from "./pages/Leagues";
import AdminOverview from "./pages/admin/AdminOverview";

// Legal pages — public, unauthenticated, brand-consistent.
import Terms from "./pages/legal/Terms";
import Privacy from "./pages/legal/Privacy";
import ChildrenPrivacy from "./pages/legal/ChildrenPrivacy";
import RefundPolicy from "./pages/legal/RefundPolicy";
import CommunityPolicy from "./pages/legal/CommunityPolicy";
import AiDisclaimer from "./pages/legal/AiDisclaimer";
import DeleteData from "./pages/legal/DeleteData";

import "./App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div style={{ padding: 24, textAlign: "center", color: "var(--chalk-muted)" }}>Loading…</div>;
  }
  if (!user) {
    // Remember where they were going; the Login screen will bounce them
    // back there after a successful sign-in. Default destination is the
    // marketing landing page (`/`) so unauthenticated visitors see the
    // product story first, not a bare sign-in form.
    sessionStorage.setItem("chalkos.afterLogin", location.pathname + location.search);
    return <Navigate to="/" replace />;
  }
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  if (user?.role === "guardian") return <Clipboard />;
  return <Chalkboard />;
}

const TOAST_OPTIONS = {
  style: {
    background: "var(--chalk-board)",
    color: "var(--chalk-white)",
    border: "0.5px solid rgba(245,240,232,0.10)",
    borderRadius: 12,
    fontSize: 14,
  },
};

function App() {
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/sw.js").catch(() => {});
      });
    }
  }, []);

  return (
    <AuthProvider>
      <TeamProvider>
        <BrowserRouter>
          <Toaster
            position="top-center"
            toastOptions={TOAST_OPTIONS}
          />
          <RouterShell />
        </BrowserRouter>
      </TeamProvider>
    </AuthProvider>
  );
}

// RouterShell lives inside BrowserRouter so it can subscribe to route changes
// via useLocation (through the chalk-erase-reveal hook). The hook returns a
// class name that paints a quick chalk-streak wipe on the screen each time
// the pathname changes — purely decorative, pointer-events: none, honors
// prefers-reduced-motion.
function RouterShell() {
  const revealClass = useChalkEraseReveal();
  return (
    <div className={revealClass}>
      <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/coaches" element={<Coaches />} />
            <Route path="/parents" element={<Parents />} />
            <Route path="/team-staff" element={<TeamStaff />} />
            <Route path="/leagues" element={<Leagues />} />
            <Route path="/tournaments" element={SHOW_TOURNAMENT_MARKETING ? <TournamentsLanding /> : <Navigate to="/" replace />} />

            {/* Legal — public, brand-consistent. */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/children-privacy" element={<ChildrenPrivacy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/community-policy" element={<CommunityPolicy />} />
            <Route path="/ai-disclaimer" element={<AiDisclaimer />} />
            <Route path="/delete-data" element={<DeleteData />} />

            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Login />} />
            <Route path="/forgot" element={<Forgot />} />
            <Route path="/reset" element={<ResetPassword />} />
            <Route path="/join" element={<Join />} />
            <Route path="/join/team" element={<JoinTeam />} />
            <Route path="/staff/accept" element={<StaffAccept />} />
            <Route path="/start" element={<Start />} />
            <Route path="/t/:slug" element={<PublicTournament />} />
            <Route path="/t/:slug/checkin/:token" element={<PublicCheckin />} />
            <Route path="/t/:slug/results" element={<PublicResults />} />
            <Route path="/t/:slug/screen" element={<BigScreen />} />
            <Route path="/claim/:token" element={<ClaimTeam />} />
            <Route path="/screen" element={<ScreenPair />} />
            <Route path="/d/:handle" element={<DirectorArchive />} />
            <Route path="/billing/tournament-checkout" element={<Protected><TournamentCheckoutMock /></Protected>} />
            <Route path="/print/tournaments/:id/:kind" element={<Protected><TournamentPrint /></Protected>} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />

            <Route path="/app" element={<Protected><AppShell /></Protected>}>
              <Route index element={<RoleHome />} />
              <Route path="playbook" element={<Playbook />} />
              <Route path="roster" element={<Roster />} />
              <Route path="family" element={<FamilyDashboard />} />
              <Route path="talk" element={<ChalkTalk />} />
              <Route path="files" element={<ChalkLines />} />
              <Route path="volunteers" element={<Volunteers />} />
              <Route path="league" element={<League />} />
              <Route path="tournaments" element={<Tournaments />} />
              <Route path="tournaments/:id" element={<TournamentWorkspace />} />
              <Route path="tournaments/:id/upgrade" element={<TournamentUpgrade />} />
              <Route path="director" element={<DirectorPlan />} />
              <Route path="more" element={<More />} />
              <Route path="upgrade" element={<Upgrade />} />
            </Route>

            <Route path="/admin" element={<Protected><AdminOverview /></Protected>} />
            <Route path="/admin/overview" element={<Protected><AdminOverview /></Protected>} />
            <Route path="/admin/billing" element={<Protected><AdminBilling /></Protected>} />
            <Route path="/admin/notifications" element={<Protected><AdminNotifications /></Protected>} />
            <Route path="/admin/emails" element={<Protected><AdminEmails /></Protected>} />
            <Route path="/admin/jobs" element={<Protected><AdminJobs /></Protected>} />
            <Route path="/admin/audit" element={<Protected><AdminAudit /></Protected>} />
            <Route path="/admin/permission-tests" element={<Protected><AdminPermissionTests /></Protected>} />
            <Route path="/admin/voluntold" element={<Protected><AdminVoluntold /></Protected>} />

            <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
            <Route path="/billing/preview-checkout" element={<Protected><PreviewCheckout /></Protected>} />
            <Route path="/billing/cancelled" element={<Protected><BillingCancelled /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
    </div>
  );
}

export default App;
